import React, { useState, useRef } from "react";
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
} from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import ScrollReveal from "./ScrollReveal";
import Footer from "./Footer";
import { ArrowLeft } from "lucide-react";

const AIPenetrationTesting = () => {
  const navigate = useNavigate();
  // Reference for the methodology section to track scroll progress
  const timelineRef = useRef(null);

  // Captures scroll progress specifically within this container
  const { scrollYProgress } = useScroll({
    target: timelineRef,
    offset: ["start center", "end center"],
  });

  // Transforms the scroll progress into a height scale percentage (from 0% to 100%)
  const scaleY = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  const tabsData = [
    {
      id: "tab1",
      title: "Holistic Coverage",
      contentTitle: "Holistic Coverage",
      description:
        "Evaluate your entire AI ecosystem, not just isolated model behavior.",
    },
    {
      id: "tab2",
      title: "Real-World Attack Simulation",
      contentTitle: "Real-World Attack Simulation",
      description:
        "Uncover systemic faults attackers will chain together, revealing risks missed by basic testing.",
    },
    {
      id: "tab3",
      title: "Cutting-Edge Expertise",
      contentTitle: "Cutting-Edge Expertise",
      description:
        "Leverage our deep research, proprietary taxonomy, and experience with the latest AI technologies.",
    },
    {
      id: "tab4",
      title: "Actionable Insights",
      contentTitle: "Actionable Insights",
      description:
        "Receive clear, prioritized findings with practical remediation steps tailored to your environment.",
    },
    {
      id: "tab5",
      title: "Build Trust & Ensure Compliance",
      contentTitle: "Build Trust & Ensure Compliance",
      description:
        "Fortify your AI systems to prevent data breaches, maintain regulatory compliance, avoid reputational damage, and build user confidence.",
    },
  ];

  const [activeTab, setActiveTab] = useState("tab1");

  const methodologyData = [
    {
      title: "System Input Identification",
      description:
        "Thoroughly map all entry points user interfaces (UIs), APIs, file uploads, tool calls, third-party integrations, and implicit channels (e.g., agent-to-agent communications, microservices) to understand how data reaches the model and identify potential injection vectors.",
    },
    {
      title: "Ecosystem Attack Simulation",
      description:
        "Probe the surrounding infrastructure and dependencies orchestration platforms, model-hosting environments, micro-services, vector databases, metadata stores, logging pipelines, third-party APIs, and auxiliary services assessing access controls and identifying systemic weaknesses.",
    },
    {
      title: "Model Security Assessment",
      description:
        "Directly assess the AI model itself for vulnerabilities beyond just undesirable content. This includes jailbreaks, policy/safety-filter bypasses, analyzing model versioning, parameter configurations, rate limits, gradient exploits, fine tune extraction, data poisoning risks, model inversion attempts, and bias amplification.",
    },
    {
      title: "Prompt Engineering Exploitation",
      description:
        "Leverage advanced techniques to test how the AI interprets and executes instructions. This includes logic bombs, context window stuffing, hidden-channel abuse, autonomous agent prompt poisoning, and bypassing designed guardrails through carefully crafted inputs, utilizing our custom Synthera Security Prompt Injection Taxonomy.",
    },
    {
      title: "Data Security and Integrity Analysis",
      description:
        "Evaluate the security of data pipelines, training/embedding sets, Retrieval Augmented Generation (RAG) sources, and data processing.",
    },
    {
      title: "Application Security Testing",
      description:
        "Assess the security of the software application housing or interacting with the AI model. Identify traditional vulnerabilities (e.g., SSRF, XSS, command execution, IDOR in chat UIs, OWASP Top 10) amplified by the AI context, function calling abuse, insecure output handling, and more.",
    },
    {
      title: "Pivoting & Lateral Movement",
      description:
        "Simulate how real-world attackers chain vulnerabilities and leverage initial footholds (e.g., successful prompt injection) to gain deeper access, escalate privileges, move laterally to adjacent SaaS, cloud, or on-prem assets, and impact other parts of the system or network, demonstrating real business impact (e.g., sensitive data theft, production code commits).",
    },
  ];

  return (
    <div className="bg-[#011916] text-white min-h-screen font-sans overflow-x-hidden selection:bg-[#048649] selection:text-white">
      <button
        onClick={() => navigate(-1)}
        className="absolute top-20 left-8 z-50 flex items-center justify-center w-10 h-10 rounded-full bg-black/20 hover:bg-black/40 text-white/70 hover:text-[#24ff81] transition-all duration-300"
      >
        <ArrowLeft size={20} />
      </button>

      {/* SECTION 1: TOP HERO HEADER SECTION */}
      <ScrollReveal>
        <div className="pt-32 pb-16 px-6 max-w-[1600px] mx-auto">
          <section className="relative bg-[#022722] rounded-[3rem] p-8 md:p-10 lg:p-12 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
            {/* LEFT CONTENT */}
            <div className="md:col-span-6">
              <h1 className="text-3xl md:text-5xl font-black text-white leading-tight mb-4 tracking-tight">
                AI Penetration Testing <br />
                and Red Teaming
              </h1>

              <h4 className="text-white text-lg md:text-xl font-extrabold mb-5 tracking-wide uppercase">
                Comprehensive Security for AI-Enabled Systems
              </h4>

              <p className="text-gray-300 text-base md:text-lg leading-[1.8] mb-10 max-w-2xl">
                In an era where Artificial Intelligence (AI) and Large Language
                Models (LLMs) are increasingly integrated into critical business
                operations, traditional security testing falls short. Standard
                "AI red teaming" often narrowly focuses on provoking undesirable
                outputs from the model itself.
                <br />
                <br />
                Synthera Security’s AI Penetration Testing Service redefines AI
                security assessments by adopting a holistic, ecosystem-wide
                approach. We scrutinize every layer of your AI-enabled
                applications, from user inputs and data pipelines to the
                surrounding infrastructure and downstream business workflows,
                identifying vulnerabilities that simplistic model testing
                overlooks.
              </p>

              <Link
                to="/contact"
                className="bg-[#048649] hover:bg-[#036c3a] text-white font-bold text-sm md:text-base px-7 py-3 rounded-md transition-all duration-200 w-fit no-underline shadow-lg shadow-green-950/20"
              >
                Work with Us
              </Link>
            </div>

            {/* RIGHT IMAGE */}
            <div className="md:col-span-6 flex justify-center w-full">
              <div className="w-[90%] rounded-2xl overflow-hidden shadow-2xl shadow-green-950/10">
                <img
                  src="https://res.cloudinary.com/dpn3bkjba/image/upload/f_auto,q_auto:good,w_1200/v1779089479/penetartion_vgvtvs.png"
                  alt="AI Red Teaming Visual representation"
                  className="w-full h-[600px] object-cover rounded-2xl"
                />
              </div>
            </div>
          </section>
        </div>
      </ScrollReveal>
      {/* SECTION 2: EXPERIENCE THE DIFFERENCE (TAB SELECTOR) */}
      <ScrollReveal>
        <section className="relative bg-[#011916] py-20 px-6 max-w-6xl mx-auto">
          <div className="text-center md:text-left mb-12">
            <span className="text-gray-300 text-xs font-bold uppercase tracking-wider block mb-1">
              Why Choose Synthera Security?
            </span>
            <h2 className="text-2xl md:text-4xl font-black text-white uppercase tracking-tight">
              Experience the Difference
            </h2>
            <p className="text-gray-300 text-xs md:text-sm mt-2">
              Synthera Security operates at the forefront of AI security
              research and practice. Our edge comes from:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
            {/* Left Button Controls Menu */}
            <div className="md:col-span-5 flex flex-col gap-2">
              {tabsData.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full text-left px-5 py-3.5 rounded-lg border text-xs md:text-sm font-bold transition-all duration-200 ${
                    activeTab === tab.id
                      ? "bg-[#04433c] text-emerald-300 border-emerald-500/30 shadow-md"
                      : "bg-[#021f1b] text-gray-400 border-white/5 hover:bg-[#022722] hover:text-white"
                  }`}
                >
                  {tab.title}
                </button>
              ))}
            </div>

            {/* Right Display Presentation Panel */}
            <div className="md:col-span-7 h-full min-h-[200px]">
              <div className="bg-[#021f1b] border border-white/5 rounded-xl p-8 h-full flex flex-col justify-center relative min-h-[220px]">
                <AnimatePresence mode="wait">
                  {tabsData.map(
                    (tab) =>
                      tab.id === activeTab && (
                        <motion.div
                          key={tab.id}
                          initial={{ opacity: 0, x: 8 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -8 }}
                          transition={{ duration: 0.15 }}
                        >
                          <h3 className="text-lg md:text-xl font-bold text-white mb-3">
                            {tab.contentTitle}
                          </h3>
                          <p className="text-gray-300 text-xs md:text-sm leading-relaxed">
                            {tab.description}
                          </p>
                        </motion.div>
                      ),
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* SECTION 3: BEYOND TRADITIONAL RED TEAMING EXPOSITION */}
      <ScrollReveal>
        <section className="bg-[#011916] py-16 px-6 text-center border-t border-white/5">
          <div className="max-w-3xl mx-auto">
            <span className="text-gray-300 text-xs font-semibold uppercase tracking-wider block mb-1">
              Our Approach
            </span>
            <h2 className="text-xl md:text-3xl font-black text-white tracking-tight uppercase mb-4">
              Beyond Traditional Red Teaming
            </h2>
            <p className="text-gray-300 text-xs md:text-sm leading-relaxed">
              Malicious actors don't just target the AI model in isolation; they
              exploit the entire interconnected system. Our methodology
              replicates this full attack kill-chain, assessing how AI
              integrates with your business and simulating sophisticated attacks
              that target real-world vulnerabilities across the entire stack
              APIs, plugins, data stores, users, and connected systems. We
              provide assurance far beyond basic "jailbreak" exercises.
            </p>
          </div>
        </section>
      </ScrollReveal>

      {/* SECTION 4: SEVEN-PILLAR ASSESSMENT METHODOLOGY TIMELINE */}
      <ScrollReveal>
        <section
          ref={timelineRef}
          className="bg-[#031513] pb-32 pt-6 px-6 max-w-4xl mx-auto relative rounded-2xl"
        >
          <div className="mb-16 text-center md:text-left">
            <span className="text-gray-300 text-xs font-semibold uppercase tracking-wider block mb-1">
              Our Seven-Pillar Assessment
            </span>
            <h2 className="text-xl md:text-3xl font-black text-white uppercase tracking-tight">
              Methodology
            </h2>
          </div>

          {/* Timeline Wrapper Grid Layout */}
          <div className="relative pl-6 md:pl-10">
            {/* Background Base Muted Track Line */}
            <div className="absolute left-1.5 md:left-3 top-2 bottom-2 w-[1px] bg-[#021f1b]" />

            {/* DYNAMIC SCROLLING GREEN ACCENT LINE STEM */}
            <motion.div
              style={{ height: scaleY }}
              className="absolute left-1.5 md:left-3 top-2 w-[1px] bg-emerald-500 origin-top z-10 shadow-[0_0_8px_rgba(16,185,129,0.5)]"
            />

            <div className="flex flex-col gap-12 relative">
              {methodologyData.map((item, index) => (
                <div key={index} className="w-full relative">
                  {/* Dynamic Glowing Green Circle Node Marker */}
                  <motion.div
                    initial={{ scale: 0.6, backgroundColor: "#021f1b" }}
                    whileInView={{ scale: 1, backgroundColor: "#048649" }}
                    viewport={{ once: false, amount: "even" }}
                    transition={{ duration: 0.3 }}
                    className="absolute -left-[23px] md:-left-[35px] w-2.5 h-2.5 top-1.5 rounded-full z-20 ring-4 ring-[#032d28]"
                  />
                  {/* Dynamic Text Group revealing on Scroll viewport arrival */}

                  <motion.div
                    initial={{ opacity: 0, y: 30, scale: 0.98 }}
                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                    viewport={{ once: false, amount: 0.2 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                  >
                    <h3 className="text-base md:text-lg font-bold text-white mb-2 tracking-tight transition-colors duration-300 group-hover:text-emerald-400">
                      {item.title}
                    </h3>
                    <p className="text-gray-300 text-xs md:text-sm leading-relaxed max-w-3xl font-light">
                      {item.description}
                    </p>
                  </motion.div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>
      <Footer />
    </div>
  );
};

export default AIPenetrationTesting;
